/**
 * The Meta Pixel and the Google tag, loaded only after the visitor presses Accept.
 *
 * Nothing in here runs at import. `loadAdTags()` is called by the consent banner once the choice
 * is "granted", and every tracking helper checks that the tags were actually loaded first, so a
 * visitor who rejected (or never answered) sends nothing to either company.
 *
 * The ids come from public env vars, inlined at build time:
 *   NEXT_PUBLIC_META_PIXEL_ID              the Meta dataset ("Ownly Digital", 831970528048735)
 *   NEXT_PUBLIC_GOOGLE_ADS_ID              "AW-..." once a Google Ads account exists
 *   NEXT_PUBLIC_GOOGLE_ADS_BOOKING_LABEL   the label of the "booked a call" conversion action
 * Any that are unset are skipped. With none set there is no banner at all (see lib/consent.ts).
 *
 * WHY NOT next/script OR @next/third-parties. Both load on mount. The whole point here is that
 * the network request itself waits for a yes, so the scripts are injected by hand after one.
 */

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: Fbq;
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export const META_PIXEL_ID = (process.env.NEXT_PUBLIC_META_PIXEL_ID ?? "").trim();
export const GOOGLE_ADS_ID = (process.env.NEXT_PUBLIC_GOOGLE_ADS_ID ?? "").trim();
export const GOOGLE_ADS_BOOKING_LABEL = (process.env.NEXT_PUBLIC_GOOGLE_ADS_BOOKING_LABEL ?? "").trim();

/** Only digits: a stray quote or space in the env var must not end up inside a script URL. */
export function validMetaPixelId(id: string): boolean {
  return /^\d{6,20}$/.test(id);
}

export function validGoogleAdsId(id: string): boolean {
  return /^AW-\d{6,15}$/.test(id);
}

/** The companies whose tags this build would load, named the way the banner says them. */
export function configuredAdVendors(
  metaId: string = META_PIXEL_ID,
  googleId: string = GOOGLE_ADS_ID,
): string[] {
  const vendors: string[] = [];
  if (validMetaPixelId(metaId)) vendors.push("Meta");
  if (validGoogleAdsId(googleId)) vendors.push("Google");
  return vendors;
}

/**
 * The id both sides use for one booking, so Meta counts it once.
 *
 * The browser sees the invitee uuid in Calendly's `calendly.event_scheduled` message; Lead Engine
 * sees the same uuid at the end of the invitee uri in the `invitee.created` webhook. Both build
 * this same string, and Meta drops whichever copy of the Schedule event arrives second.
 */
export function bookingEventId(inviteeUuid: string): string {
  return `calendly-${inviteeUuid.toLowerCase()}`;
}

let loaded = false;

function injectScript(src: string): void {
  if (document.querySelector(`script[src="${src}"]`)) return;
  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

/** Meta's own base code, rewritten as a function so it cannot run before consent. */
function startMetaPixel(id: string): void {
  if (!window.fbq) {
    const fbq = function (...args: unknown[]) {
      if (fbq.callMethod) fbq.callMethod(...args);
      else fbq.queue.push(args);
    } as Fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    window.fbq = fbq;
    window._fbq = fbq;
  }
  injectScript("https://connect.facebook.net/en_US/fbevents.js");
  window.fbq("init", id);
  window.fbq("track", "PageView");
}

function startGoogleTag(id: string): void {
  window.dataLayer = window.dataLayer ?? [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // gtag.js reads the `arguments` object itself, not an array, so this must stay a real function.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }
  /* Consent Mode v2. Declared denied first and then granted, rather than only granted, so that
     Google's own records show the grant as an explicit update from a visitor's choice. */
  window.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
  });
  window.gtag("consent", "update", {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "granted",
  });
  window.gtag("js", new Date());
  window.gtag("config", id);
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`);
}

/** Starts whichever tags are configured. Call only once the visitor has said yes. */
export function loadAdTags(): void {
  if (loaded || typeof window === "undefined") return;
  loaded = true;
  if (validMetaPixelId(META_PIXEL_ID)) startMetaPixel(META_PIXEL_ID);
  if (validGoogleAdsId(GOOGLE_ADS_ID)) startGoogleTag(GOOGLE_ADS_ID);
}

/**
 * Tells the tags already on this page to stop. The page is reloaded straight after a withdrawal,
 * which is what actually unloads them; this covers the moment in between.
 */
export function revokeAdTags(): void {
  if (!loaded) return;
  window.fbq?.("consent", "revoke");
  window.gtag?.("consent", "update", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
  });
}

export function adTagsLoaded(): boolean {
  return loaded;
}

/** A client-side route change. The first page view is sent by `loadAdTags` itself. */
export function trackAdPageView(): void {
  if (!loaded) return;
  window.fbq?.("track", "PageView");
  if (validGoogleAdsId(GOOGLE_ADS_ID)) {
    window.gtag?.("event", "page_view", { send_to: GOOGLE_ADS_ID });
  }
}

/** Someone just booked a call. Sent to both tags, carrying the id Lead Engine uses too. */
export function trackBookingConversion(eventId: string): void {
  if (!loaded) return;
  window.fbq?.("track", "Schedule", {}, { eventID: eventId });
  if (validGoogleAdsId(GOOGLE_ADS_ID) && GOOGLE_ADS_BOOKING_LABEL) {
    window.gtag?.("event", "conversion", {
      send_to: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_BOOKING_LABEL}`,
      transaction_id: eventId,
    });
  }
}
